import { useState, useEffect, useCallback, useMemo } from 'react';
import useIsomorphicLayoutEffect from '@hooks/useIsomorphicLayoutEffect';
import { isNumber } from 'lodash';
import { isSSR } from '@helpers';

const useMatchMedia = isSSR() ? useDefaultValues : useMediaQueries;
export default useMatchMedia;

function useDefaultValues(...args) {
  return args[2] ?? false;
}

function useMediaQueries(queries, value = true, defaultValue = false) {
  const valuesArr = useMemo(() => {
    return Array.isArray(value) ? value : [value];
  }, [value]);

  const mediaQueries = useMemo(() => {
    return [queries]
      .flat()
      .map(q => {
        if (isNumber(q)) {
          q = `(max-width: ${q}px)`;
        } else if (!q) {
          return null;
        }

        q = q.includes('(') ? q : `(${q})`;

        return window.matchMedia(q);
      })
      .filter(q => q);
  }, [queries]);

  const getValue = useCallback(() => {
    const index = mediaQueries.findIndex(m => m.matches);
    return index === -1 ? defaultValue : valuesArr[index];
  }, [valuesArr, mediaQueries, defaultValue]);

  const [currentValue, setCurrentValue] = useState(getValue());

  useIsomorphicLayoutEffect(() => {
    const handler = () => setCurrentValue(getValue);
    mediaQueries.forEach(m => m.addListener(handler));
    return () => mediaQueries.forEach(m => m.removeListener(handler));
  }, [mediaQueries, getValue]);

  // Force recheck query one time to ensure
  // the correct value displays after SSR
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    let timer = null;

    if (!hasInitialized) {
      setHasInitialized(true);

      timer = setTimeout(() => {
        setCurrentValue(getValue);
      }, 5);
    }

    return () => clearTimeout(timer);
  }, [hasInitialized, getValue]);

  return currentValue;
}
